import styled from "styled-components";
import { Button, Flex, Heading, useBreakpointValue } from "@chakra-ui/react";
import { useEffect, useRef } from "react";
import { useGameContext } from "../../../context/gameContext/gameContext";
import selectChoice from "../../../assets/selectChoice.wav";
import { pickBestTrumpHeuristic } from "../../../utils/ai/botController";

import { GiClubs, GiSpades, GiHearts, GiDiamonds } from "react-icons/gi";

const TrumpOptions = ({ beginTrickRound, winningBiddersHand, botTrumpChoice }) => {
  const { setTrumpSuit } = useGameContext();
  const iconSize = useBreakpointValue({ base: 20, md: 28, lg: 35 }) ?? 35;

  function playSound() {
    const audio = new Audio(selectChoice);
    audio.play();
  }

  const submitTrump = (e) => {
    playSound();
    setTrumpSuit(e.currentTarget.name);
    // update players cards to prepresent trump
    beginTrickRound(e.currentTarget.name);
  };

  const botActingRef = useRef(false);

  // If a bot seat won the bid, pick its best trump suit automatically.
  useEffect(() => {
    if (!winningBiddersHand || winningBiddersHand.length === 0) return;
    const owner = winningBiddersHand[0]?.details?.owner;
    if (owner === "P1" || botActingRef.current) return;

    botActingRef.current = true;
    const timer = setTimeout(() => {
      const suit = botTrumpChoice || pickBestTrumpHeuristic(winningBiddersHand);
      botActingRef.current = false;
      submitTrump({ currentTarget: { name: suit } });
    }, 700);

    return () => {
      clearTimeout(timer);
      botActingRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [winningBiddersHand]);

  // Bots pick their own trump automatically (handled by the effect above) -
  // only the human needs to see this UI. The component still needs to stay
  // mounted for a bot's turn so that effect can run.
  const isHumanTurn = winningBiddersHand?.[0]?.details?.owner === "P1";
  if (!isHumanTurn) return null;

  const suits = ["hearts", "spades", "clubs", "diamonds"];
  const missingSuits = suits.filter(
    (suit) => !winningBiddersHand.some((card) => card.suit === suit)
  );

  return (
    <Wrapper>
      <Flex direction="column" gap="30px" alignItems="center" px={2}>
        <Heading size={{ base: "md", md: "lg", lg: "xl" }} textAlign="center">
          Choose trump suit
        </Heading>

        <Flex justify={"center"} gap={"30px"} wrap="wrap">
          <Button
            onClick={submitTrump}
            name="diamonds"
            isDisabled={missingSuits.includes("diamonds")}
            sx={{
              _hover: missingSuits.includes("diamonds")
                ? {}
                : { backgroundColor: "gray.200" },
              _disabled: {
                cursor: "default",
                backgroundColor: "#e5e7eb",
                color: "#93979f",
                borderColor: "#9c9ea3",
              },
            }}
          >
            <GiDiamonds color={"red"} size={iconSize} />
          </Button>
          <Button
            onClick={submitTrump}
            name="clubs"
            isDisabled={missingSuits.includes("clubs")}
            sx={{
              _hover: missingSuits.includes("clubs")
                ? {}
                : { backgroundColor: "gray.200" },
              _disabled: {
                cursor: "default",
                backgroundColor: "#e5e7eb",
                color: "#93979f",
                borderColor: "#9c9ea3",
              },
            }}
          >
            <GiClubs size={iconSize} />
          </Button>
          <Button
            onClick={submitTrump}
            name="hearts"
            isDisabled={missingSuits.includes("hearts")}
            sx={{
              _hover: missingSuits.includes("hearts")
                ? {}
                : { backgroundColor: "gray.200" },
              _disabled: {
                cursor: "default",
                backgroundColor: "#e5e7eb",
                color: "#93979f",
                borderColor: "#9c9ea3",
              },
            }}
          >
            <GiHearts color={"red"} size={iconSize} />
          </Button>
          <Button
            onClick={submitTrump}
            name="spades"
            isDisabled={missingSuits.includes("spades")}
            sx={{
              _hover: missingSuits.includes("spades")
                ? {}
                : { backgroundColor: "gray.200" },
              _disabled: {
                cursor: "default",
                backgroundColor: "#e5e7eb",
                color: "#93979f",
                borderColor: "#9c9ea3",
              },
            }}
          >
            <GiSpades size={iconSize} />
          </Button>
        </Flex>
      </Flex>
    </Wrapper>
  );
};

export default TrumpOptions;

const Wrapper = styled.div`
  position: relative;
  z-index: 3;

  Button {
    width: clamp(38px, 10vw, 80px);
    height: clamp(32px, 8vw, 60px);
    font-size: clamp(13px, 3.5vw, 20px);
    border: 1px solid black;
    background-color: white;
  }
`;
